import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { usePendingApprovalsForSession } from '~/features/chat/pending-approval-card';
import { useChatStatuses } from '~/hooks/queries/use-chat-status';
import type { HandsFreeCallHooks } from '../use-voice-call';
import { approvalWatchSessionIds, quietPhase } from './screen';

/**
 * The parts of a hands-free call every surface runs the same way (FRONT-254,
 * shared with the phone by FRONT-3268): the activity clock the call reports
 * into, the screen note sent when live and on each change, and the quiet
 * clock that ends a call nobody is using — held open while tool work or an
 * approval in the call's chat is pending. What is on screen and where a
 * screen move goes stay with each surface.
 */

/** How long screen changes settle before EDDY is told about the new screen. */
const SCREEN_NOTE_DEBOUNCE_MS = 500;
const SCREEN_NOTE_RETRY_MS = 250;
const SCREEN_NOTE_MAX_TRIES = 40;
const QUIET_TICK_MS = 1_000;

/** The call's activity clock and the hooks that feed it. Reset on every new call. */
export function useHandsFreeActivity() {
  const lastActivityRef = useRef(Date.now());
  const workRef = useRef(0);
  const report = useMemo<Pick<HandsFreeCallHooks, 'onActivity' | 'onWorkChange'>>(
    () => ({
      onActivity: () => {
        lastActivityRef.current = Date.now();
      },
      onWorkChange: (delta) => {
        workRef.current = Math.max(0, workRef.current + delta);
        lastActivityRef.current = Date.now();
      },
    }),
    [],
  );
  /** A new call starts with no work in flight from the last one. */
  const reset = useCallback(() => {
    workRef.current = 0;
    lastActivityRef.current = Date.now();
  }, []);
  return { lastActivityRef, workRef, report, reset };
}

/**
 * Tell EDDY what is on screen: once when live, then on each change. The data
 * channel opens a round trip or two after the call reads as live, so a note
 * that finds it still connecting is retried (bounded) rather than dropped.
 */
export function useScreenNoteSender(note: string, live: boolean, sendScreenNote: (note: string) => boolean): void {
  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!live) {
      lastSentRef.current = null;
      return;
    }
    if (note === lastSentRef.current) return;
    let tries = 0;
    let t: ReturnType<typeof setTimeout>;
    const attempt = () => {
      if (sendScreenNote(note)) lastSentRef.current = note;
      else if (++tries < SCREEN_NOTE_MAX_TRIES) t = setTimeout(attempt, SCREEN_NOTE_RETRY_MS);
    };
    t = setTimeout(attempt, lastSentRef.current === null ? 0 : SCREEN_NOTE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [note, live, sendScreenNote]);
}

/**
 * The quiet clock of a live call, and the approvals of its chat that hold it
 * open. A pushed approval can land on the other replica's event stream and a
 * minimized call has no chat window polling for it, so the call polls its own
 * chat's status for as long as it is live (FRONT-3118).
 */
export function useQuietCallClock(input: {
  live: boolean;
  sessionId: string | null;
  stop: () => void;
  activity: Pick<ReturnType<typeof useHandsFreeActivity>, 'lastActivityRef' | 'workRef'>;
}) {
  const { live, sessionId, stop } = input;
  const { lastActivityRef, workRef } = input.activity;

  const watchIds = approvalWatchSessionIds(sessionId, live);
  useChatStatuses(watchIds, { pollWhile: watchIds.length > 0 });
  const pendingApprovals = usePendingApprovalsForSession(sessionId ?? '').filter((a) => a.waitingMode !== 'deferred');
  const waitingRef = useRef(false);
  waitingRef.current = sessionId !== null && pendingApprovals.length > 0;

  const [quietSecondsLeft, setQuietSecondsLeft] = useState<number | null>(null);
  const keepCall = useCallback(() => {
    lastActivityRef.current = Date.now();
    setQuietSecondsLeft(null);
  }, [lastActivityRef]);

  useEffect(() => {
    if (!live) {
      setQuietSecondsLeft(null);
      return;
    }
    lastActivityRef.current = Date.now();
    const tick = setInterval(() => {
      const now = Date.now();
      // Waiting on tool work or on the person's yes is not a quiet call.
      if (workRef.current > 0 || waitingRef.current) lastActivityRef.current = now;
      const phase = quietPhase(lastActivityRef.current, now);
      if (phase.phase === 'end') {
        setQuietSecondsLeft(null);
        toast.info('The call ended after five quiet minutes. Your recap is on its way.');
        stop();
      } else {
        setQuietSecondsLeft(phase.phase === 'warn' ? phase.secondsLeft : null);
      }
    }, QUIET_TICK_MS);
    return () => clearInterval(tick);
  }, [live, stop, lastActivityRef, workRef]);

  return { quietSecondsLeft, keepCall };
}
